import { useState } from "react";
import { PageHeader } from "@/pages/Categories";
import { CATEGORIES } from "@/data/categories";
import { api, formatErr } from "@/lib/api";
import { CheckCircle2, Upload, FileText, Loader2, X } from "lucide-react";
import { Link } from "react-router-dom";

const COUNTRIES = [
  "United Kingdom", "Germany", "France", "Italy", "Spain", "Netherlands", "Belgium", "Sweden", "Denmark",
  "Norway", "Finland", "Ireland", "Portugal", "Austria", "Switzerland", "Poland", "Czech Republic", "Greece",
  "Romania", "Hungary", "United States", "Canada", "United Arab Emirates", "Saudi Arabia", "Australia", "Other (Europe)", "Other",
];

const TIMELINES = ["Within 4 weeks", "1–2 months", "3 months", "3–6 months", "6+ months", "Just exploring"];

const initial = {
  full_name: "", company_name: "", email: "", phone: "", country: "",
  product_category: "", product_description: "", target_quantity: "",
  target_price: "", certifications: "", timeline: "", message: "",
};

export default function Quote() {
  const [form, setForm] = useState(initial);
  const [file, setFile] = useState(null);
  const [uploadInfo, setUploadInfo] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function handleFile(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) {
      setError("File is too large (max 10 MB).");
      return;
    }
    setError("");
    setUploading(true);
    setFile(f);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUploadInfo(data);
    } catch (err) {
      setError(formatErr(err.response?.data?.detail) || err.message);
      setFile(null);
      setUploadInfo(null);
    } finally {
      setUploading(false);
    }
  }

  function clearFile() {
    setFile(null);
    setUploadInfo(null);
  }

  async function submit(e) {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        file_path: uploadInfo?.path || null,
        file_name: uploadInfo?.name || null,
      };
      const { data } = await api.post("/enquiries", payload);
      setDone(data.id);
      setForm(initial);
      setFile(null);
      setUploadInfo(null);
    } catch (err) {
      setError(formatErr(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <div data-testid="quote-success">
        <PageHeader eyebrow="REQUEST RECEIVED" title="Thank you — your sourcing brief is in." />
        <section className="max-w-3xl mx-auto px-6 lg:px-8 pb-24">
          <div className="border border-emerald-500/20 bg-emerald-500/[0.04] rounded-lg p-8">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="h-6 w-6 text-emerald-400" />
              <div className="font-display text-xl font-semibold">Submission ID: <span className="font-mono text-emerald-300">{done.slice(0, 8).toUpperCase()}</span></div>
            </div>
            <p className="mt-4 text-zinc-300 text-[14.5px] leading-relaxed">
              We&apos;ve received your brief and notified our sourcing team. You&apos;ll hear back from
              <a href="mailto:reachus@vyrtrade.com" className="text-amber-400 hover:underline"> reachus@vyrtrade.com</a> typically within one working day.
            </p>
            <div className="mt-6 flex gap-3">
              <Link to="/" className="inline-flex items-center px-4 py-2.5 rounded-md border border-white/10 hover:bg-white/5 text-sm" data-testid="success-home">Back to Home</Link>
              <button onClick={() => setDone(null)} className="inline-flex items-center px-4 py-2.5 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-sm" data-testid="success-new-brief">Submit another brief</button>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="quote-page">
      <PageHeader
        eyebrow="REQUEST A QUOTE"
        title="Tell us what you need to source."
        sub="The more detail you share, the faster we can come back with a vetted shortlist and indicative prices."
      />
      <section className="max-w-5xl mx-auto px-6 lg:px-8 pb-24">
        <form onSubmit={submit} className="border border-white/10 bg-zinc-900/40 rounded-lg p-6 sm:p-8 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="quote-form">
          <Field label="Full name" required>
            <input data-testid="qf-full-name" required value={form.full_name} onChange={onChange("full_name")} className={inputCls} placeholder="Jane Doe" />
          </Field>
          <Field label="Company name" required>
            <input data-testid="qf-company" required value={form.company_name} onChange={onChange("company_name")} className={inputCls} placeholder="Acme Procurement Ltd" />
          </Field>
          <Field label="Work email" required>
            <input data-testid="qf-email" required type="email" value={form.email} onChange={onChange("email")} className={inputCls} placeholder="jane@acme.com" />
          </Field>
          <Field label="Phone / WhatsApp" required>
            <input data-testid="qf-phone" required value={form.phone} onChange={onChange("phone")} className={inputCls} placeholder="+44 7700 900000" />
          </Field>
          <Field label="Country" required>
            <select data-testid="qf-country" required value={form.country} onChange={onChange("country")} className={inputCls}>
              <option value="">Select a country</option>
              {COUNTRIES.map((c) => (<option key={c} value={c}>{c}</option>))}
            </select>
          </Field>
          <Field label="Product category" required>
            <select data-testid="qf-category" required value={form.product_category} onChange={onChange("product_category")} className={inputCls}>
              <option value="">Select a category</option>
              {CATEGORIES.map((c) => (<option key={c.slug} value={c.name}>{c.name}</option>))}
              <option value="Other / Not listed">Other / Not listed</option>
            </select>
          </Field>
          <Field label="Product description" required full>
            <textarea data-testid="qf-description" required rows={4} value={form.product_description} onChange={onChange("product_description")} className={inputCls} placeholder="What exactly are you looking to source? Materials, finish, end use, etc." />
          </Field>
          <Field label="Target quantity">
            <input data-testid="qf-quantity" value={form.target_quantity} onChange={onChange("target_quantity")} className={inputCls} placeholder="e.g. 10,000 pcs / 20 ft container" />
          </Field>
          <Field label="Target price">
            <input data-testid="qf-price" value={form.target_price} onChange={onChange("target_price")} className={inputCls} placeholder="e.g. €1.80 / unit FOB" />
          </Field>
          <Field label="Required certifications">
            <input data-testid="qf-certs" value={form.certifications} onChange={onChange("certifications")} className={inputCls} placeholder="e.g. IFRA, OEKO-TEX, GOTS, FSC, CE" />
          </Field>
          <Field label="Timeline">
            <select data-testid="qf-timeline" value={form.timeline} onChange={onChange("timeline")} className={inputCls}>
              <option value="">Select a timeline</option>
              {TIMELINES.map((t) => (<option key={t} value={t}>{t}</option>))}
            </select>
          </Field>

          <Field label="Specification / reference (PDF or image)" full>
            <label className="block border border-dashed border-white/15 hover:border-amber-400/40 bg-zinc-950 rounded-md p-5 cursor-pointer transition-colors" data-testid="qf-file-label">
              <input type="file" accept="image/*,application/pdf" onChange={handleFile} className="hidden" data-testid="qf-file-input" />
              {!file && (
                <div className="flex items-center gap-3 text-zinc-400 text-sm">
                  <Upload className="h-4 w-4" />
                  <span>Click to upload — PDF, PNG, JPG (max 10 MB)</span>
                </div>
              )}
              {file && (
                <div className="flex items-center justify-between gap-3" data-testid="qf-file-info">
                  <div className="flex items-center gap-3 text-sm">
                    {uploading ? <Loader2 className="h-4 w-4 animate-spin text-amber-400" /> : <FileText className="h-4 w-4 text-amber-400" />}
                    <div>
                      <div className="text-zinc-200">{file.name}</div>
                      <div className="text-[11px] text-zinc-500 font-mono">{Math.round(file.size / 1024)} KB · {uploading ? "Uploading…" : uploadInfo ? "Uploaded" : "Pending"}</div>
                    </div>
                  </div>
                  <button type="button" onClick={clearFile} className="text-zinc-400 hover:text-white" data-testid="qf-file-clear">
                    <X className="h-4 w-4" />
                  </button>
                </div>
              )}
            </label>
          </Field>

          <Field label="Anything else?" full>
            <textarea data-testid="qf-message" rows={3} value={form.message} onChange={onChange("message")} className={inputCls} placeholder="Context, prior suppliers, exclusivity, packing preferences, etc." />
          </Field>

          {error && (
            <div className="md:col-span-2 text-sm text-red-400 border border-red-500/30 bg-red-500/[0.04] rounded-md px-3 py-2" data-testid="qf-error">{error}</div>
          )}

          <div className="md:col-span-2 flex items-center justify-between gap-4 pt-2">
            <div className="text-[12px] text-zinc-500">By submitting you agree to our <Link to="/privacy" className="text-zinc-300 hover:text-white">Privacy Policy</Link>.</div>
            <button type="submit" disabled={submitting || uploading} className="inline-flex items-center gap-2 px-5 py-3 rounded-md bg-amber-400 text-zinc-950 hover:bg-amber-300 font-semibold text-sm disabled:opacity-60" data-testid="qf-submit">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {submitting ? "Submitting…" : "Submit sourcing brief"}
            </button>
          </div>
        </form>

        <p className="mt-5 text-[12.5px] text-zinc-500">
          VYR Trade is a sourcing & trade facilitation company — not a manufacturer, unless explicitly stated. Supplier verification, certificates, samples and compliance documents are coordinated case by case.
        </p>
      </section>
    </div>
  );
}

const inputCls =
  "w-full bg-zinc-950 border border-white/10 focus:border-amber-400/60 focus:outline-none rounded-md px-3.5 py-2.5 text-[14px] text-zinc-100 placeholder:text-zinc-600 transition-colors";

function Field({ label, required, full, children }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <label className="block text-[12px] font-mono tracking-wide text-zinc-400 mb-1.5">
        {label}{required ? <span className="text-amber-400 ml-1">*</span> : null}
      </label>
      {children}
    </div>
  );
}
